import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { EnabledFeaturesService } from '../services/EnabledFeaturesService';
import { useMplServiceClient } from './MplServiceClientContext';

const EnabledFeaturesServiceContext = createContext<EnabledFeaturesService | undefined>(undefined);

export function EnabledFeaturesProvider({ children }: { readonly children: ReactNode }) {
  const client = useMplServiceClient();
  const service = useMemo(() => new EnabledFeaturesService(client), [client]);

  // When the provider mounts (the user is authenticated by this point), then
  // load the user's enabled features once.
  useEffect(() => {
    void service.loadEnabledFeatures();
  }, [service]);

  return (
    <EnabledFeaturesServiceContext.Provider value={service}>
      {children}
    </EnabledFeaturesServiceContext.Provider>
  );
}

export function useEnabledFeaturesService(): EnabledFeaturesService {
  const context = useContext(EnabledFeaturesServiceContext);
  if (!context) {
    throw new Error('useEnabledFeaturesService must be used within EnabledFeaturesProvider');
  }
  return context;
}

/** The set of feature ids currently enabled for the user; re-renders on change. */
export function useEnabledFeatures(): ReadonlySet<string> {
  const service = useEnabledFeaturesService();

  // When the service loads or refreshes its features, then re-render with the new set.
  const subscribe = useCallback(
    (onStoreChange: () => void) => service.on('changed', onStoreChange),
    [service],
  );
  return useSyncExternalStore(subscribe, () => service.enabledFeatureIds);
}

/** Whether the given feature id is enabled for the user; re-renders on change. */
export function useIsFeatureEnabled(featureId: string): boolean {
  return useEnabledFeatures().has(featureId);
}
